import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
} from 'react';

import { Map, Iterable } from 'immutable';

import Alert        from '@mui/material/Alert';
import Grid         from '@mui/material/Grid';
import Button       from '@mui/material/Button';
import Box          from '@mui/material/Box';
import Typography   from '@mui/material/Typography';

import Upload         from '@mui/icons-material/Upload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon      from '@mui/icons-material/Close';

import Display from 'components/Generic/Display';
// Having issues with useDropzone hook so I'm using this alternative.
// Issue is on upload, still needs to click the page again to refocus.
import Dropzone from 'react-dropzone';

// value => [ { blob, url, signedBlobId? or uploaded } ]

const TYPES = [
  'text/plain',
  'text/csv',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
];

function FileUpload({
  name = null,
  value = null,
  onChange = null,
  disabled = false,
  multiple = true,
  variant = 'contained',
  label = t('eos.landing_page.label.upload'),
  types = TYPES,
}) {
  const [state, setState] = useState(new Map());

  const files = state.get('files', []);

  useEffect(() => {
    if (value === null || !value.get('blobs')) return;

    setState(state.set('files', value.get('blobs')));
  }, [value]);

  const LIMIT = multiple ? 5 : 1;

  function onDropAccepted(acceptedFiles) {
    let filtered = acceptedFiles.map((file) => ({ blob: file }));

    const total = files.length + acceptedFiles.length;

    // Remove excess files if limit was reached
    if (total > LIMIT) {
      const excess = total - LIMIT; // We need a negative number for the slice
      filtered = filtered.slice(excess);
    }

    onChange(name, new Map({ blobs: files.concat(filtered) }));
  }

  function onDropRejected(rejectedFiles) {
    const rejected = rejectedFiles.map((file) => {
      const errors = file.errors.map((e) => t(e.code.replace(/-/g, '_'), { scope: 'eos.landing_page.text.upload', defaultValue: e.message }));

      return { name: file.file.name, errors: errors.join(', ') };
    });

    onChange(name, new Map({ blobs: files.concat(rejected) }));
  }

  const dropzoneParams = {
    accept:   types.join(', '),
    minSize:  1,
    maxSize:  10000000, // 10mb
    disabled: disabled || files.length >= LIMIT,
    onDropAccepted,
    onDropRejected,
  };

  function removeFile(index) {
    const filestmp = [...files];

    filestmp.splice(index, 1);
    setState(state.set('files', filestmp));

    onChange(name, new Map({ blobs: filestmp }));
  }

  function AcceptedFiles({ idx, file }) {
    return (
      <Box
        data-testid="file"
        sx={{
          display:        'flex',
          flexDirection:  'row',
          alignItems:     'center',
          justifyContent: 'center',
          border:         1,
          borderRadius:   1,
          borderColor:    'primary.main',
          p:              1,
          mb:             1,
          cursor:         'pointer',
          overflowWrap:   'anywhere',
        }}
      >
        <UploadFileIcon color="primary" />
        <Typography variant="h5" sx={{ ml: 2 }} color="primary">{file.path}</Typography>
        <CloseIcon data-testid="remove-file" onClick={() => removeFile(idx)} color="secondary" />
      </Box>
    );
  }

  function RejectedFiles({ idx, file }) {
    return (
      <li key={idx}>
        <Typography variant="p">{file.name}</Typography>
        <ul>
          <li data-testid="file-error" key={file.name}><Typography variant="p">{file.errors}</Typography></li>
        </ul>
      </li>
    );
  }

  return (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Dropzone
            accept={dropzoneParams.accept}
            minSize={dropzoneParams.minSize}
            maxSize={dropzoneParams.maxSize}
            disabled={dropzoneParams.disabled}
            onDropAccepted={dropzoneParams.onDropAccepted}
            onDropRejected={dropzoneParams.onDropRejected}
          >
            {({ getRootProps, getInputProps }) => (
              <div data-testid="dropzone" {...getRootProps()}>
                <input {...getInputProps()} />
                <Button
                  variant={variant}
                  component="span"
                  color="primary"
                  onClick={null}
                  startIcon={variant === 'contained' ? <Upload /> : null}
                  data-testid="file-upload-upload-button"
                  disabled={dropzoneParams.disabled}
                >
                  {label}
                </Button>
              </div>
            )}
          </Dropzone>
        </Box>

        <Display if={files.filter((file) => !file.error).length > 0}>
          <Box
            data-testid="accepted-files"
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 3,
            }}
          >
            {
              files.map(
                (file, idx) => {
                  if (!file.error) {
                    return <AcceptedFiles key={file.blob.name} idx={idx} file={file.blob} />;
                  }

                  return null;
                },
              ) // To keep the global index
            }
          </Box>
        </Display>

        <Display if={files.filter((file) => file.error).length > 0}>
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="h6">{t('eos.landing_page.text.upload.error.not_supported')}</Typography>
            <ul style={{ padding: 0, margin: 0 }}>
              {
                files.filter((file) => file.error)
                  .map((file, idx) => <RejectedFiles key={file} idx={idx} file={file} />)
              }
            </ul>
          </Alert>
        </Display>
      </Box>
    </Grid>
  );
}

FileUpload.propTypes = {
  name:     PropTypes.string,
  value:    PropTypes.objectOf(Iterable),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  variant:  PropTypes.string,
  label:    PropTypes.string,
  types:    PropTypes.arrayOf(PropTypes.string),
};

export default FileUpload;
